import "./ui/styles.css";
import { renderMatrix, resolveContentFromSelection, type MatrixController, type Selection } from "./ui/matrix";
import { renderResults } from "./ui/results-panel";
import {
  ajudaContentHtml,
  renderEvolutionSection,
  renderFooter,
  renderIntro,
  renderMobileWarning,
  renderOriginPanel,
  renderTopBar,
  showModal,
  sobreContentHtml,
} from "./ui/shell";
import { withWeight } from "./domain/classification";
import type { ArchetypeId, RelevanceId } from "./domain/types";
import {
  clearState,
  deleteContentNode,
  deleteInfluenceNode,
  generateId,
  loadContentNodes,
  loadInfluenceNodes,
  nextColor,
  saveContentNode,
  saveInfluenceNode,
} from "./state/content-store";

const app = document.getElementById("app");
if (!app) throw new Error("#app not found");

renderTopBar(app, {
  onNovo(): void {
    if (loadContentNodes().length === 0) return; // nothing to lose, no need to confirm
    const confirmed = window.confirm("Limpar o projeto atual e começar uma nova sessão de planejamento?");
    if (!confirmed) return;
    clearState();
    selection = null;
    matrixController.refresh();
    rerenderAll();
  },
  onImprimir(): void {
    window.print();
  },
  onAjuda(): void {
    showModal("Ajuda", ajudaContentHtml());
  },
  onSobre(): void {
    showModal("Sobre", sobreContentHtml());
  },
});

// Matrix comes immediately after the top bar, with no title above it — it
// occupies the first fold (design D19). "Metodologia AV" intro text moved
// below the results panel (see renderIntro() call further down).
const matrixSection = document.createElement("section");
matrixSection.id = "matriz";
const matrixContainer = document.createElement("div");
matrixContainer.className = "container";
matrixSection.appendChild(matrixContainer);
app.appendChild(matrixSection);

const matrixHost = document.createElement("div");
matrixHost.className = "matrix-host";
matrixContainer.appendChild(matrixHost);

const resultsSection = document.createElement("section");
resultsSection.id = "resultado";
resultsSection.innerHTML = `<div class="container results-host"></div>`;
app.appendChild(resultsSection);
const resultsHost = resultsSection.querySelector<HTMLElement>(".results-host");
if (!resultsHost) throw new Error("results host not found");
const resolvedResultsHost: HTMLElement = resultsHost;

// Below 767px, #matriz/#resultado are hidden by CSS and this renders in
// their place instead — same DOM position, so it never ends up after the
// footer (see design.md, fix-metodologia-smartphone-warning-placement).
renderMobileWarning(app);

renderIntro(app);
renderOriginPanel(app);
renderEvolutionSection(app);
renderFooter(app);

let selection: Selection = null;
let matrixController: MatrixController;

function rerenderAll(): void {
  // Every content's result block is listed — not just the selected one
  // (design D26). Selection only decides which block gets highlighted.
  renderResults(resolvedResultsHost, loadContentNodes(), resolveContentFromSelection(selection)?.id ?? null, onRenameContent);
}

// Design D33: click-to-rename via the results panel's own block title —
// double-clicking the matrix square is still there, but the author
// reported it "não deu certo" (two ordinary clicks, outside the browser's
// dblclick timing window, register as nothing). Renaming updates both the
// results panel AND the matrix (the SVG label under the square reads the
// same `content.label`), via the same refresh()/rerenderAll() pair every
// other edit already uses.
function onRenameContent(contentId: string, newLabel: string): void {
  const content = loadContentNodes().find((c) => c.id === contentId);
  if (!content || content.label === newLabel) return;
  saveContentNode({ ...content, label: newLabel });
  matrixController.refresh();
  rerenderAll();
}

function promptWeight(currentWeight: 1 | 2 | 3): 1 | 2 | 3 {
  const input = window.prompt("Peso (1 = baixo, 2 = médio, 3 = alto):", String(currentWeight));
  const parsed = Number(input);
  return parsed === 1 || parsed === 2 || parsed === 3 ? parsed : currentWeight;
}

matrixController = renderMatrix(matrixHost, {
  onSelect(next): void {
    selection = next;
    rerenderAll();
  },

  onCellActivate(position: { archetypeId: ArchetypeId; relevance: RelevanceId }): void {
    // Clicking (or keyboard-activating) an empty matrix cell creates a
    // content exactly there — no default position that could collide with
    // wherever the user actually meant to place real content.
    const label = window.prompt("Nome do novo conteúdo:", "Novo conteúdo");
    if (!label) return;
    const content = {
      id: generateId("content"),
      label,
      archetypeId: position.archetypeId,
      relevance: position.relevance,
      weight: 2 as const,
      color: nextColor(),
    };
    saveContentNode(content);
    selection = { type: "content", id: content.id };
    matrixController.refresh();
    rerenderAll();
  },

  onCreateInfluence(contentId: string): void {
    const parent = loadContentNodes().find((c) => c.id === contentId);
    if (!parent) return;
    const influence = {
      id: generateId("influence"),
      contentId: parent.id,
      archetypeId: parent.archetypeId,
      relevance: parent.relevance,
      // "ao clicar, aparece a janela do peso, que determina o tamanho do círculo" —
      // weight is asked immediately at creation time.
      weight: promptWeight(2),
    };
    saveInfluenceNode(influence);
    selection = { type: "influence", id: influence.id };
    matrixController.refresh();
    rerenderAll();
  },

  onEditContent(contentId: string): void {
    const content = loadContentNodes().find((c) => c.id === contentId);
    if (!content) return;
    const newLabel = window.prompt("Nome:", content.label) ?? content.label;
    const weight = promptWeight(content.weight);
    saveContentNode({ ...content, label: newLabel, weight });
    matrixController.refresh();
    rerenderAll();
  },

  onEditInfluence(influenceId: string): void {
    const influence = loadInfluenceNodes().find((i) => i.id === influenceId);
    if (!influence) return;
    saveInfluenceNode(withWeight(influence, promptWeight(influence.weight)));
    matrixController.refresh();
    rerenderAll();
  },

  onDeleteContent(contentId: string): void {
    deleteContentNode(contentId); // cascades: influences + relationships referencing it
    selection = null;
    matrixController.refresh();
    rerenderAll();
  },

  onDeleteInfluence(influenceId: string): void {
    deleteInfluenceNode(influenceId);
    selection = null;
    matrixController.refresh();
    rerenderAll();
  },
});

rerenderAll();

// Print (design D21/D22) changes .matrix-grid's own row/column sizing (plain
// block flow + auto height, not the screen-only viewport-filling layout) —
// but the SVG overlay's node coordinates are computed once via JS, from the
// grid's actual measured pixel size (matrix-geometry.ts's
// getBoundingClientRect()-based computeCellRects()). A CSS media query alone
// doesn't retrigger that computation, so switching to the print stylesheet
// left the overlay's squares/circles positioned for the screen layout while
// the underlying cells had already changed size — misaligned, overlapping.
// Redrawing on beforeprint/afterprint keeps the overlay in sync with
// whichever layout (screen or print) is actually active; this covers
// Ctrl+P/Cmd+P too, not just the "Imprimir" button above.
window.addEventListener("beforeprint", () => matrixController.refresh());
window.addEventListener("afterprint", () => matrixController.refresh());
